import { Sparkles } from "lucide-react";
import { type FormEvent, useState } from "react";

import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ApiError, api } from "@/lib/api";
import type { KbView } from "@/lib/types";
import { cn } from "@/lib/utils";

import { ALL_TEMPLATES } from "./templates";
import type { WorkshopPage } from "./types";

type Mode = "auto" | "template";

interface WorkshopAnalyzeWire extends Omit<WorkshopPage, "id" | "created_at" | "updated_at"> {
  source: "llm" | "heuristic";
}

interface CreatePageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kbs: KbView[];
  defaultKbId: string | null;
  onCreate: (draft: Omit<WorkshopPage, "id" | "created_at" | "updated_at">) => void | Promise<void>;
}

/**
 * "New page" dialog with two affordances:
 *
 *  - **Describe** — free text. With a selected KB we call
 *    ``POST /v1/workshop/analyze`` to pull live document + search data
 *    and synthesize a real dashboard (LLM when a preset is available,
 *    otherwise a deterministic layout).
 *  - **Pick a template** — explicit gallery for users who already know
 *    what shape they want.
 *
 * The dialog never persists on its own; it returns a freshly built
 * page draft via ``onCreate`` and lets the caller decide where to put
 * it.
 */
export function CreatePageDialog({
  open,
  onOpenChange,
  kbs,
  defaultKbId,
  onCreate,
}: CreatePageDialogProps) {
  const [mode, setMode] = useState<Mode>("auto");
  const [prompt, setPrompt] = useState("");
  const [title, setTitle] = useState("");
  const [templateId, setTemplateId] = useState<string>(ALL_TEMPLATES[0].id);
  const [kbId, setKbId] = useState<string>(defaultKbId ?? "__none__");

  const [busy, setBusy] = useState(false);

  const reset = (): void => {
    setMode("auto");
    setPrompt("");
    setTitle("");
    setTemplateId(ALL_TEMPLATES[0].id);
    setKbId(defaultKbId ?? "__none__");
    setBusy(false);
  };

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (mode === "auto") {
      if (kbId === "__none__") {
        toast.error("请选择一个知识库，系统需要从真实 KB 拉取文档与检索结果");
        return;
      }
      setBusy(true);
      try {
        const analyzed = await api<WorkshopAnalyzeWire>("/workshop/analyze", {
          method: "POST",
          body: JSON.stringify({
            kb_id: kbId,
            prompt: prompt.trim(),
          }),
        });
        const finalTitle = title.trim() || prompt.trim() || analyzed.title;
        const { source: analyzedSource, ...draft } = analyzed;
        await onCreate({
          ...draft,
          title: finalTitle,
          source_prompt: prompt.trim(),
          last_analyze_source: analyzedSource,
        });
        toast.success(
          analyzed.source === "llm"
            ? "已结合知识库数据与模型生成页面"
            : "已根据知识库列表与检索结果生成页面",
        );
        reset();
        onOpenChange(false);
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : "生成失败，请稍后再试");
      } finally {
        setBusy(false);
      }
      return;
    }

    const tpl = ALL_TEMPLATES.find((t) => t.id === templateId) ?? ALL_TEMPLATES[0];
    const kb =
      kbId === "__none__"
        ? { id: null, name: null }
        : (() => {
            const found = kbs.find((k) => k.id === kbId);
            return { id: kbId, name: found?.name ?? null };
          })();
    const built = tpl.build(kb);
    await onCreate({ ...built, title: built.title });
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-xl">
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>新建 Workshop 页面</DialogTitle>
            <DialogDescription>
              「描述需求」会调用服务端：读取该知识库的文档列表并对你的描述做向量检索，生成真实数据页面（需已配置
              LLM 预设时附带模型解读）。也可改用「选择模板」从本地模板快速搭页面。
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-2">
            <Label className="text-xs">关联知识库</Label>
            <Select value={kbId} onValueChange={setKbId}>
              <SelectTrigger>
                <SelectValue placeholder="选择 KB" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">不绑定特定 KB</SelectItem>
                {kbs.map((kb) => (
                  <SelectItem key={kb.id} value={kb.id}>
                    {kb.name}{" "}
                    <span className="text-[11px] text-muted-foreground">({kb.scope})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1 rounded-md bg-muted p-1 text-[12px]">
            <button
              type="button"
              onClick={() => setMode("auto")}
              className={cn(
                "flex-1 rounded px-3 py-1 transition-colors",
                mode === "auto"
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <Sparkles className="mr-1 inline h-3 w-3" />
              描述需求
            </button>
            <button
              type="button"
              onClick={() => setMode("template")}
              className={cn(
                "flex-1 rounded px-3 py-1 transition-colors",
                mode === "template"
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              选择模板
            </button>
          </div>

          {mode === "auto" ? (
            <div className="space-y-3">
              <div className="grid gap-2">
                <Label className="text-xs" htmlFor="workshop-prompt">
                  你想看什么？
                </Label>
                <Textarea
                  id="workshop-prompt"
                  rows={3}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="比如：过去一个月的收益情况；或：近期 KB 里有哪些新动态"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label className="text-xs" htmlFor="workshop-title">
                  页面标题（可选）
                </Label>
                <Input
                  id="workshop-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="留空则用上面的描述"
                />
              </div>
              <div className="rounded-md border border-dashed border-border bg-muted/40 p-3 text-[12px] leading-relaxed text-muted-foreground">
                将请求 <span className="font-medium text-foreground">/v1/workshop/analyze</span>{" "}
                ：汇总文档状态、近日上传趋势、检索片段等；团队下若存在可用的 LLM
                预设，会进一步生成文字与版式建议。
              </div>
            </div>
          ) : (
            <div className="grid gap-2">
              <Label className="text-xs">模板</Label>
              <div className="grid gap-2">
                {ALL_TEMPLATES.map((tpl) => (
                  <button
                    type="button"
                    key={tpl.id}
                    onClick={() => setTemplateId(tpl.id)}
                    className={cn(
                      "rounded-lg border p-3 text-left transition-colors",
                      templateId === tpl.id
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted",
                    )}
                  >
                    <div className="text-[13px] font-semibold">{tpl.label}</div>
                    <div className="mt-0.5 text-[11.5px] text-muted-foreground">
                      {tpl.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              取消
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? "生成中…" : "创建"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
